import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { LogOut, LayoutDashboard, ClipboardList, Users, UserCog, FileText, Plus, Trash2, ExternalLink, X, BadgeCheck, Loader2, Calendar, Phone, MapPin, FileSignature, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, API, TOKEN_KEY, formatApiError } from "../lib/api";
import Logo from "../components/Logo";

const STATUS_OPTIONS = ["new", "confirmed", "scheduled", "in_progress", "completed", "cancelled"];
const STATUS_LABELS = {
    new: "Neu",
    confirmed: "Bestätigt",
    scheduled: "Geplant",
    in_progress: "Läuft",
    completed: "Erledigt",
    cancelled: "Storniert",
};
const STATUS_COLORS = {
    new: "bg-blue-50 text-blue-700 border-blue-200",
    confirmed: "bg-violet-50 text-violet-700 border-violet-200",
    scheduled: "bg-amber-50 text-amber-700 border-amber-200",
    in_progress: "bg-orange-50 text-orange-700 border-orange-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};
const SERVICE_LABELS = {
    privat_haushalt: "Wohnungsreinigung",
    buero: "Büro",
    gebaeude: "Gebäude",
    scheiben: "Scheiben & Schaufenster",
    begleitservice: "Begleitservice",
};

function NavItem({ active, onClick, icon: Icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                active ? "bg-[#1A365D] text-white shadow-sm" : "text-[#1A365D]/70 hover:bg-slate-100"
            }`}
        >
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

function Modal({ open, onClose, title, children, testId }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-[#0B1B33]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testId}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
                    <h3 className="font-display font-bold text-lg text-[#1A365D]">{title}</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-[#1A365D]" data-testid="modal-close-button"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

// ----- Overview -----
function Overview() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/stats/overview").then(({ data }) => setStats(data)).catch(() => {});
    }, []);
    const Card = ({ label, value, accent }) => (
        <div className={`rounded-2xl border ${accent ? "border-[#16A34A]/30 bg-gradient-to-br from-[#F0FDF4] to-white" : "border-slate-200 bg-white"} p-6`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs uppercase tracking-wider text-[#64748B] font-bold">{label}</div>
            <div className="font-display font-black text-3xl text-[#1A365D] mt-2">{value ?? "—"}</div>
        </div>
    );
    return (
        <div className="space-y-6" data-testid="overview-panel">
            <h2 className="font-display font-bold text-2xl text-[#1A365D]">Übersicht</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card label="Neue Buchungen" value={stats?.bookings_new} accent />
                <Card label="Geplant" value={stats?.bookings_scheduled} />
                <Card label="Erledigt" value={stats?.bookings_completed} />
                <Card label="Total Buchungen" value={stats?.bookings_total} />
                <Card label="Kunden" value={stats?.customers} />
                <Card label="Mitarbeiter" value={stats?.employees} />
                <Card label="Rechnungen" value={stats?.invoices} />
                <Card label="Umsatz CHF" value={stats ? stats.revenue_chf.toFixed(2) : "—"} accent />
            </div>
        </div>
    );
}

// ----- Bookings -----
function Bookings({ employees, setOfferteFor }) {
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bookings");
            setRows(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const update = async (id, payload) => {
        try {
            await api.patch(`/bookings/${id}`, payload);
            toast.success("Aktualisiert");
            await load();
            if (selected) {
                const { data } = await api.get(`/bookings/${id}`);
                setSelected(data);
            }
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Buchung wirklich löschen?")) return;
        try {
            await api.delete(`/bookings/${id}`);
            toast.success("Gelöscht");
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="space-y-6" data-testid="bookings-panel">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-2xl text-[#1A365D]">Buchungen / Aufträge</h2>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="bookings-table">
                        <thead className="bg-slate-50 text-left">
                            <tr>
                                <th className="px-4 py-3 font-semibold text-[#1A365D]">Kunde</th>
                                <th className="px-4 py-3 font-semibold text-[#1A365D]">Service</th>
                                <th className="px-4 py-3 font-semibold text-[#1A365D]">Wunschdatum</th>
                                <th className="px-4 py-3 font-semibold text-[#1A365D]">Status</th>
                                <th className="px-4 py-3 font-semibold text-[#1A365D]">Team</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Laden…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Noch keine Buchungen.</td></tr>
                            ) : rows.map((b) => (
                                <tr key={b.id} className="hover:bg-slate-50" data-testid={`booking-row-${b.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold text-[#1A365D]">{b.customer_name}</div>
                                        <div className="text-xs text-slate-500">{b.customer_phone}</div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">{SERVICE_LABELS[b.service_type] || b.service_type}</td>
                                    <td className="px-4 py-3 text-slate-700">{b.preferred_date}{b.preferred_time ? `, ${b.preferred_time}` : ""}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2.5 py-1 text-xs font-bold rounded-full border ${STATUS_COLORS[b.status]}`}>{STATUS_LABELS[b.status]}</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600">
                                        {(b.assigned_employee_ids || []).length === 0 ? "—" : (b.assigned_employee_ids || []).map((id) => employees.find((e) => e.id === id)?.name || "?").join(", ")}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => setSelected(b)} className="text-[#1A365D] hover:text-[#16A34A] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`booking-open-${b.id}`}>
                                            Öffnen <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal open={!!selected} onClose={() => setSelected(null)} title={`Buchung — ${selected?.customer_name || ""}`} testId="booking-detail-modal">
                {selected ? (
                    <div className="space-y-5 text-sm">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="rounded-xl bg-slate-50 p-3">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Kunde</div>
                                <div className="font-semibold text-[#1A365D]">{selected.customer_name}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Telefon</div>
                                <div className="font-semibold text-[#1A365D]">{selected.customer_phone}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 col-span-2">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">E-Mail</div>
                                <div className="font-semibold text-[#1A365D]">{selected.customer_email || "—"}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3 col-span-2">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Adresse</div>
                                <div className="font-semibold text-[#1A365D]">{selected.address}, {selected.postal_code} {selected.city}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Service</div>
                                <div className="font-semibold text-[#1A365D]">{SERVICE_LABELS[selected.service_type]}</div>
                            </div>
                            <div className="rounded-xl bg-slate-50 p-3">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Wunschdatum</div>
                                <div className="font-semibold text-[#1A365D]">{selected.preferred_date}{selected.preferred_time ? ` · ${selected.preferred_time}` : ""}</div>
                            </div>
                            {selected.message && (
                            <div className="rounded-xl bg-slate-50 p-3 col-span-2">
                                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Nachricht</div>
                                <div className="text-[#1A365D]">{selected.message}</div>
                            </div>
                        )}
                        {selected.service_details && Object.keys(selected.service_details).length > 0 && (
                            <div className="rounded-xl bg-[#F0FDF4] border border-[#16A34A]/20 p-3 col-span-2" data-testid="booking-detail-service-details">
                                <div className="text-[10px] uppercase font-bold text-[#16A34A] tracking-wider mb-1.5">Vom Kunden angegebene Details</div>
                                <div className="text-[#0F172A] text-xs space-y-0.5">
                                    {Object.entries(selected.service_details).map(([k, v]) => {
                                        if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                                        const display = Array.isArray(v) ? v.join(", ") : String(v);
                                        return <div key={k}><strong>{k}:</strong> {display}</div>;
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Status</label>
                            <select
                                value={selected.status}
                                onChange={(e) => update(selected.id, { status: e.target.value })}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5"
                                data-testid="booking-status-select"
                            >
                                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Geplant Datum</label>
                                <input
                                    type="date"
                                    defaultValue={selected.scheduled_date || ""}
                                    onBlur={(e) => e.target.value !== (selected.scheduled_date || "") && update(selected.id, { scheduled_date: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5"
                                    data-testid="booking-scheduled-date-input"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Geplant Zeit</label>
                                <input
                                    type="time"
                                    defaultValue={selected.scheduled_time || ""}
                                    onBlur={(e) => e.target.value !== (selected.scheduled_time || "") && update(selected.id, { scheduled_time: e.target.value })}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5"
                                    data-testid="booking-scheduled-time-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Preis (CHF)</label>
                            <input
                                type="number" step="0.01"
                                defaultValue={selected.price_chf || ""}
                                onBlur={(e) => Number(e.target.value) !== (selected.price_chf || 0) && update(selected.id, { price_chf: Number(e.target.value) || 0 })}
                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5"
                                data-testid="booking-price-input"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-2">Team zuweisen</label>
                            <div className="flex flex-wrap gap-2">
                                {employees.length === 0 ? (
                                    <span className="text-xs text-slate-500">Keine Mitarbeiter angelegt.</span>
                                ) : employees.map((e) => {
                                    const active = (selected.assigned_employee_ids || []).includes(e.id);
                                    return (
                                        <button
                                            key={e.id}
                                            type="button"
                                            onClick={() => {
                                                const ids = active
                                                    ? selected.assigned_employee_ids.filter((id) => id !== e.id)
                                                    : [...(selected.assigned_employee_ids || []), e.id];
                                                update(selected.id, { assigned_employee_ids: ids });
                                            }}
                                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? "bg-[#16A34A] text-white border-[#16A34A]" : "bg-white text-[#1A365D] border-slate-200 hover:border-[#16A34A]"}`}
                                            data-testid={`assign-emp-${e.id}`}
                                        >
                                            {active ? <BadgeCheck className="w-3.5 h-3.5 inline mr-1" /> : null}{e.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="pt-3 border-t border-slate-200 flex flex-wrap gap-3 justify-between">
                            <button onClick={() => { remove(selected.id); setSelected(null); }} className="text-red-600 hover:text-red-700 text-xs font-semibold inline-flex items-center gap-1" data-testid="booking-delete-button">
                                <Trash2 className="w-4 h-4" /> Löschen
                            </button>
                            <div className="flex gap-2">
                                <button onClick={() => { setOfferteFor(selected); setSelected(null); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#16A34A] text-white text-xs font-semibold hover:bg-[#15803D]" data-testid="booking-create-offerte-button">
                                    <FileSignature className="w-4 h-4" /> Offerte erstellen
                                </button>
                                <button onClick={() => setSelected(null)} className="btn-secondary !px-5 !py-2" data-testid="booking-close-button">Schliessen</button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </Modal>
        </div>
    );
}

// ----- Customers -----
function Customers() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "" });

    const load = async () => {
        try {
            const { data } = await api.get("/customers");
            setRows(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (!payload.email) delete payload.email;
            await api.post("/customers", payload);
            setOpen(false);
            setForm({ name: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "" });
            toast.success("Kunde angelegt");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Kunde löschen?")) return;
        await api.delete(`/customers/${id}`);
        toast.success("Gelöscht");
        load();
    };

    const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm";

    return (
        <div className="space-y-6" data-testid="customers-panel">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-2xl text-[#1A365D]">Kunden</h2>
                <button onClick={() => setOpen(true)} className="btn-primary !px-5 !py-2.5" data-testid="add-customer-button"><Plus className="w-4 h-4" /> Neu</button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="customers-table">
                    <thead className="bg-slate-50 text-left">
                        <tr>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Name</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Kontakt</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Adresse</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">Keine Kunden.</td></tr>
                        ) : rows.map((c) => (
                            <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                                <td className="px-4 py-3 font-semibold text-[#1A365D]">{c.name}</td>
                                <td className="px-4 py-3 text-slate-700">{c.phone || "—"}<br /><span className="text-xs text-slate-500">{c.email || ""}</span></td>
                                <td className="px-4 py-3 text-slate-700">{c.address || "—"}{c.city ? `, ${c.postal_code || ""} ${c.city}` : ""}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => remove(c.id)} className="text-red-600 hover:text-red-700" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title="Neuer Kunde" testId="customer-modal">
                <form onSubmit={create} className="space-y-4" data-testid="customer-form">
                    <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="customer-name-input" />
                    <input placeholder="E-Mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="customer-email-input" />
                    <input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="customer-phone-input" />
                    <input placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="customer-address-input" />
                    <div className="grid grid-cols-2 gap-3">
                        <input placeholder="PLZ" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} className={inputCls} data-testid="customer-postal-input" />
                        <input placeholder="Ort" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="customer-city-input" />
                    </div>
                    <textarea placeholder="Notizen" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="customer-notes-input" />
                    <button type="submit" className="btn-primary w-full" data-testid="customer-save-button">Speichern</button>
                </form>
            </Modal>
        </div>
    );
}

// ----- Employees -----
function Employees({ employees, reload }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
    const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm";

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/employees", form);
            setOpen(false);
            setForm({ name: "", email: "", phone: "", password: "" });
            toast.success("Mitarbeiter erstellt");
            reload();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Mitarbeiter löschen?")) return;
        await api.delete(`/employees/${id}`);
        toast.success("Gelöscht");
        reload();
    };

    return (
        <div className="space-y-6" data-testid="employees-panel">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-2xl text-[#1A365D]">Mitarbeiter</h2>
                <button onClick={() => setOpen(true)} className="btn-primary !px-5 !py-2.5" data-testid="add-employee-button"><Plus className="w-4 h-4" /> Neu</button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="employees-table">
                    <thead className="bg-slate-50 text-left">
                        <tr>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Name</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">E-Mail</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Telefon</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {employees.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-500">Keine Mitarbeiter.</td></tr>
                        ) : employees.map((e) => (
                            <tr key={e.id} className="hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                                <td className="px-4 py-3 font-semibold text-[#1A365D]">{e.name}</td>
                                <td className="px-4 py-3 text-slate-700">{e.email}</td>
                                <td className="px-4 py-3 text-slate-700">{e.phone || "—"}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => remove(e.id)} className="text-red-600 hover:text-red-700" data-testid={`employee-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title="Neuer Mitarbeiter" testId="employee-modal">
                <form onSubmit={create} className="space-y-4" data-testid="employee-form">
                    <input required placeholder="Name" value={form.name} onChange={(ev) => setForm({ ...form, name: ev.target.value })} className={inputCls} data-testid="employee-name-input" />
                    <input required type="email" placeholder="E-Mail" value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} className={inputCls} data-testid="employee-email-input" />
                    <input placeholder="Telefon" value={form.phone} onChange={(ev) => setForm({ ...form, phone: ev.target.value })} className={inputCls} data-testid="employee-phone-input" />
                    <input required type="password" placeholder="Passwort" value={form.password} onChange={(ev) => setForm({ ...form, password: ev.target.value })} className={inputCls} data-testid="employee-password-input" />
                    <button type="submit" className="btn-primary w-full" data-testid="employee-save-button">Speichern</button>
                </form>
            </Modal>
        </div>
    );
}

// ----- Invoices -----
function Invoices({ bookings }) {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ booking_id: "", amount_chf: "", tax_percent: 8.1, due_days: 30, notes: "" });

    const load = async () => {
        const { data } = await api.get("/invoices");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/invoices", {
                booking_id: form.booking_id,
                amount_chf: Number(form.amount_chf),
                tax_percent: Number(form.tax_percent),
                due_days: Number(form.due_days),
                notes: form.notes,
            });
            setOpen(false);
            setForm({ booking_id: "", amount_chf: "", tax_percent: 8.1, due_days: 30, notes: "" });
            toast.success("Rechnung erstellt");
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        if (!window.confirm("Rechnung löschen?")) return;
        await api.delete(`/invoices/${id}`);
        toast.success("Gelöscht");
        load();
    };

    const downloadPdf = async (inv) => {
        const token = localStorage.getItem(TOKEN_KEY);
        try {
            const res = await fetch(`${API}/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Download fehlgeschlagen");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.invoice_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm";

    return (
        <div className="space-y-6" data-testid="invoices-panel">
            <div className="flex items-center justify-between">
                <h2 className="font-display font-bold text-2xl text-[#1A365D]">Rechnungen</h2>
                <button onClick={() => setOpen(true)} className="btn-primary !px-5 !py-2.5" data-testid="add-invoice-button"><Plus className="w-4 h-4" /> Neu</button>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="invoices-table">
                    <thead className="bg-slate-50 text-left">
                        <tr>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Nr.</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Kunde</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Datum</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Keine Rechnungen.</td></tr>
                        ) : rows.map((inv) => (
                            <tr key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                                <td className="px-4 py-3 font-mono font-semibold text-[#1A365D]">{inv.invoice_number}</td>
                                <td className="px-4 py-3 text-slate-700">{inv.customer_name}</td>
                                <td className="px-4 py-3 text-slate-700">{inv.issued_at?.slice(0, 10)}</td>
                                <td className="px-4 py-3 font-semibold text-[#1A365D]">CHF {inv.total_chf.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right space-x-3">
                                    <button onClick={() => downloadPdf(inv)} className="text-[#16A34A] hover:underline text-xs font-semibold" data-testid={`invoice-pdf-${inv.id}`}>PDF</button>
                                    <button onClick={() => remove(inv.id)} className="text-red-600 hover:text-red-700" data-testid={`invoice-delete-${inv.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Modal open={open} onClose={() => setOpen(false)} title="Neue Rechnung" testId="invoice-modal">
                <form onSubmit={create} className="space-y-4" data-testid="invoice-form">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Buchung</label>
                        <select required value={form.booking_id} onChange={(e) => setForm({ ...form, booking_id: e.target.value })} className={inputCls} data-testid="invoice-booking-select">
                            <option value="">Buchung wählen…</option>
                            {bookings.map((b) => (
                                <option key={b.id} value={b.id}>{b.customer_name} — {SERVICE_LABELS[b.service_type]} — {b.preferred_date}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Betrag CHF</label>
                            <input required type="number" step="0.01" value={form.amount_chf} onChange={(e) => setForm({ ...form, amount_chf: e.target.value })} className={inputCls} data-testid="invoice-amount-input" />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">MwSt %</label>
                            <input type="number" step="0.01" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} className={inputCls} data-testid="invoice-tax-input" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 mb-1.5">Zahlungsziel (Tage)</label>
                        <input type="number" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} className={inputCls} data-testid="invoice-due-input" />
                    </div>
                    <textarea placeholder="Anmerkungen" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="invoice-notes-input" />
                    <button type="submit" className="btn-primary w-full" data-testid="invoice-save-button">Rechnung erstellen</button>
                </form>
            </Modal>
        </div>
    );
}

// ----- Offerten -----
const PRICE_HINT = {
    privat_haushalt: 39,
    scheiben: 43,
    begleitservice: 36,
    buero: 39,
    gebaeude: 43,
};

function OfferteModal({ booking, onClose, onCreated }) {
    const defaultPrice = PRICE_HINT[booking?.service_type] ?? 39;
    const [items, setItems] = useState([
        { description: SERVICE_LABELS[booking?.service_type] || "Reinigung", qty: 2, unit_price_chf: defaultPrice },
    ]);
    const [flatPrice, setFlatPrice] = useState("");
    const [taxPercent, setTaxPercent] = useState(8.1);
    const [validDays, setValidDays] = useState(30);
    const [notes, setNotes] = useState("");
    const [proposedDate, setProposedDate] = useState(booking?.preferred_date || "");
    const [proposedTime, setProposedTime] = useState(booking?.preferred_time || "");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setProposedDate(booking?.preferred_date || "");
        setProposedTime(booking?.preferred_time || "");
    }, [booking?.id, booking?.preferred_date, booking?.preferred_time]);

    const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm";

    const updateItem = (i, key, val) => {
        setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
    };
    const addItem = () => setItems((a) => [...a, { description: "", qty: 1, unit_price_chf: defaultPrice }]);
    const removeItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

    const itemsTotal = items.reduce((sum, it) => sum + Number(it.qty || 0) * Number(it.unit_price_chf || 0), 0);
    const net = flatPrice ? Number(flatPrice) : itemsTotal;
    const tax = (net * Number(taxPercent || 0)) / 100;
    const total = net + tax;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                booking_id: booking.id,
                items: items.map((i) => ({ description: i.description, qty: Number(i.qty), unit_price_chf: Number(i.unit_price_chf) })),
                tax_percent: Number(taxPercent),
                valid_days: Number(validDays),
                flat_price_chf: flatPrice ? Number(flatPrice) : null,
                notes,
                proposed_date: proposedDate || null,
                proposed_time: proposedTime || null,
            };
            await api.post("/offerten", payload);
            toast.success(booking.customer_email ? "Offerte erstellt und an Kunde versandt" : "Offerte erstellt (PDF im Tab Offerten herunterladen)");
            onCreated?.();
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    return (
        <Modal open={!!booking} onClose={onClose} title={`Offerte für ${booking?.customer_name || ""}`} testId="offerte-modal">
            <form onSubmit={submit} className="space-y-4" data-testid="offerte-form">
                {booking?.service_details && Object.keys(booking.service_details).length > 0 && (
                    <div className="rounded-xl bg-[#F0FDF4] border border-[#16A34A]/20 p-3 text-xs">
                        <div className="font-bold text-[#16A34A] uppercase tracking-wider mb-1.5">Kunden-Angaben</div>
                        <div className="space-y-0.5 text-[#0F172A]">
                            {Object.entries(booking.service_details).map(([k, v]) => {
                                if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                                return <div key={k}><strong>{k}:</strong> {Array.isArray(v) ? v.join(", ") : String(v)}</div>;
                            })}
                        </div>
                    </div>
                )}

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-[#1A365D]/70">Positionen</label>
                        <button type="button" onClick={addItem} className="text-xs font-semibold text-[#16A34A] inline-flex items-center gap-1" data-testid="offerte-add-item">
                            <Plus className="w-3.5 h-3.5" /> Position
                        </button>
                    </div>
                    <div className="space-y-2">
                        {items.map((it, i) => (
                            <div key={i} className="grid grid-cols-[1fr_70px_90px_28px] gap-2 items-center" data-testid={`offerte-item-${i}`}>
                                <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Beschreibung" className={inputCls} />
                                <input type="number" step="0.5" value={it.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} placeholder="Menge" className={inputCls} />
                                <input type="number" step="0.01" value={it.unit_price_chf} onChange={(e) => updateItem(i, "unit_price_chf", e.target.value)} placeholder="CHF" className={inputCls} />
                                <button type="button" onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700" data-testid={`offerte-remove-item-${i}`}><X className="w-4 h-4" /></button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl bg-slate-50 p-3">
                    <label className="text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 block mb-1.5">Pauschalpreis (optional, überschreibt Summe)</label>
                    <input type="number" step="0.01" value={flatPrice} onChange={(e) => setFlatPrice(e.target.value)} placeholder={`Auto: CHF ${itemsTotal.toFixed(2)}`} className={inputCls} data-testid="offerte-flat-price-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 block mb-1.5">MwSt %</label>
                        <input type="number" step="0.01" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} className={inputCls} data-testid="offerte-tax-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 block mb-1.5">Gültig (Tage)</label>
                        <input type="number" value={validDays} onChange={(e) => setValidDays(e.target.value)} className={inputCls} data-testid="offerte-valid-days-input" />
                    </div>
                </div>

                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                    <label className="text-xs font-bold uppercase tracking-wider text-amber-700 block mb-2">Terminvorschlag an Kunde</label>
                    <div className="grid grid-cols-2 gap-2">
                        <input type="date" value={proposedDate} onChange={(e) => setProposedDate(e.target.value)} className={inputCls} data-testid="offerte-proposed-date-input" />
                        <input type="time" value={proposedTime} onChange={(e) => setProposedTime(e.target.value)} className={inputCls} data-testid="offerte-proposed-time-input" />
                    </div>
                    <p className="text-[11px] text-amber-700 mt-2">Der Kunde sieht diesen Vorschlag in der Annahme-E-Mail und kann ihn bestätigen oder einen anderen Termin vorschlagen.</p>
                </div>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#1A365D]/70 block mb-1.5">Anmerkungen</label>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z.B. Anfahrt inkl., …" className={inputCls} data-testid="offerte-notes-input" />
                </div>

                <div className="rounded-xl bg-[#1A365D] text-white p-4">
                    <div className="flex justify-between text-sm"><span>Netto</span><span>CHF {net.toFixed(2)}</span></div>
                    <div className="flex justify-between text-sm text-white/70"><span>MwSt</span><span>CHF {tax.toFixed(2)}</span></div>
                    <div className="flex justify-between font-display font-bold text-xl text-[#4ADE80] mt-2 pt-2 border-t border-white/10"><span>Total</span><span>CHF {total.toFixed(2)}</span></div>
                </div>

                {!booking?.customer_email && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
                        ⚠️ Keine E-Mail des Kunden hinterlegt — Offerte wird erstellt, aber nicht automatisch versandt. PDF im Offerten-Tab herunterladen.
                    </div>
                )}

                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="offerte-submit-button">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {booking?.customer_email ? "Offerte erstellen & per E-Mail senden" : "Offerte erstellen"}
                </button>
            </form>
        </Modal>
    );
}

function Offerten() {
    const [rows, setRows] = useState([]);

    const load = async () => {
        const { data } = await api.get("/offerten");
        setRows(data);
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Offerte löschen?")) return;
        await api.delete(`/offerten/${id}`);
        toast.success("Gelöscht");
        load();
    };

    const convertToInvoice = async (off) => {
        if (off.invoice_id) {
            toast.info(`Bereits umgewandelt in Rechnung`);
            return;
        }
        if (!window.confirm(`Offerte ${off.offerte_number} in Rechnung umwandeln und an Kunde senden?`)) return;
        try {
            const { data } = await api.post(`/offerten/${off.id}/convert-to-invoice`);
            toast.success(`Rechnung ${data.invoice_number} erstellt${off.customer_email ? " und versandt" : ""}`);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const downloadPdf = async (off) => {
        const token = localStorage.getItem(TOKEN_KEY);
        try {
            const res = await fetch(`${API}/offerten/${off.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("Download fehlgeschlagen");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${off.offerte_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) { toast.error(err.message); }
    };

    return (
        <div className="space-y-6" data-testid="offerten-panel">
            <h2 className="font-display font-bold text-2xl text-[#1A365D]">Offerten</h2>
            <p className="text-sm text-[#64748B]">Offerten erstellen Sie direkt aus einer Buchung (Tab "Buchungen" → öffnen → "Offerte erstellen").</p>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="offerten-table">
                    <thead className="bg-slate-50 text-left">
                        <tr>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Nr.</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Kunde</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Datum</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Gültig bis</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Total</th>
                            <th className="px-4 py-3 font-semibold text-[#1A365D]">Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {rows.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Keine Offerten.</td></tr>
                        ) : rows.map((o) => {
                            const accepted = o.status === "accepted";
                            return (
                            <tr key={o.id} className="hover:bg-slate-50" data-testid={`offerte-row-${o.id}`}>
                                <td className="px-4 py-3 font-mono font-semibold text-[#1A365D]">{o.offerte_number}</td>
                                <td className="px-4 py-3 text-slate-700">{o.customer_name}</td>
                                <td className="px-4 py-3 text-slate-700">{o.issued_at?.slice(0, 10)}</td>
                                <td className="px-4 py-3 text-slate-700">{o.valid_until}</td>
                                <td className="px-4 py-3 font-semibold text-[#1A365D]">CHF {o.total_chf.toFixed(2)}</td>
                                <td className="px-4 py-3 text-xs">
                                    {accepted ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-200" data-testid={`offerte-status-accepted-${o.id}`}>
                                            ✓ Angenommen · {o.accepted_date}{o.accepted_time ? ` ${o.accepted_time}` : ""}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold border border-amber-200">
                                            ⏳ Versandt
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                                    <button onClick={() => convertToInvoice(o)} disabled={!!o.invoice_id} className={`text-xs font-semibold ${o.invoice_id ? "text-slate-400 cursor-not-allowed" : "text-[#1A365D] hover:text-[#16A34A]"}`} data-testid={`offerte-convert-${o.id}`}>
                                        {o.invoice_id ? "✓ Rechnung" : "→ Rechnung"}
                                    </button>
                                    <button onClick={() => downloadPdf(o)} className="text-[#16A34A] hover:underline text-xs font-semibold" data-testid={`offerte-pdf-${o.id}`}>PDF</button>
                                    <button onClick={() => remove(o.id)} className="text-red-600 hover:text-red-700" data-testid={`offerte-delete-${o.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ----- Main dashboard -----
export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("overview");
    const [employees, setEmployees] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [offerteFor, setOfferteFor] = useState(null);

    const loadEmployees = async () => {
        try { const { data } = await api.get("/employees"); setEmployees(data); } catch {}
    };
    const loadBookings = async () => {
        try { const { data } = await api.get("/bookings"); setBookings(data); } catch {}
    };
    useEffect(() => { loadEmployees(); loadBookings(); }, [tab]);

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
            <div className="max-w-[1500px] mx-auto px-4 lg:px-8 py-6 grid lg:grid-cols-[260px_1fr] gap-6">
                <aside className="lg:sticky lg:top-6 lg:self-start bg-white rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-4 px-2">
                        <Link to="/" className="flex items-center gap-2" data-testid="admin-logo-link">
                            <Logo variant="light" size="sm" />
                        </Link>
                        <span className="text-[10px] uppercase tracking-wider text-[#16A34A] font-bold whitespace-nowrap">Admin</span>
                    </div>

                    <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-1 px-1 pb-1 lg:pb-0">
                        <NavItem active={tab === "overview"} onClick={() => setTab("overview")} icon={LayoutDashboard} label="Übersicht" testId="nav-overview" />
                        <NavItem active={tab === "bookings"} onClick={() => setTab("bookings")} icon={ClipboardList} label="Buchungen" testId="nav-bookings" />
                        <NavItem active={tab === "offerten"} onClick={() => setTab("offerten")} icon={FileSignature} label="Offerten" testId="nav-offerten" />
                        <NavItem active={tab === "customers"} onClick={() => setTab("customers")} icon={Users} label="Kunden" testId="nav-customers" />
                        <NavItem active={tab === "employees"} onClick={() => setTab("employees")} icon={UserCog} label="Mitarbeiter" testId="nav-employees" />
                        <NavItem active={tab === "invoices"} onClick={() => setTab("invoices")} icon={FileText} label="Rechnungen" testId="nav-invoices" />
                    </div>

                    <div className="hidden lg:block pt-4 mt-4 border-t border-slate-200">
                        <div className="px-2 mb-3 text-xs">
                            <div className="font-semibold text-[#1A365D]">{user?.name}</div>
                            <div className="text-slate-500">{user?.email}</div>
                        </div>
                        <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50" data-testid="admin-logout-button">
                            <LogOut className="w-4 h-4" /> Abmelden
                        </button>
                    </div>
                    <button onClick={logout} className="lg:hidden mt-3 ml-2 inline-flex items-center gap-2 text-xs font-semibold text-red-600 hover:underline" data-testid="admin-logout-button-mobile">
                        <LogOut className="w-3.5 h-3.5" /> Abmelden
                    </button>
                </aside>

                <main className="min-w-0">
                    {tab === "overview" && <Overview />}
                    {tab === "bookings" && <Bookings employees={employees} setOfferteFor={setOfferteFor} />}
                    {tab === "offerten" && <Offerten />}
                    {tab === "customers" && <Customers />}
                    {tab === "employees" && <Employees employees={employees} reload={loadEmployees} />}
                    {tab === "invoices" && <Invoices bookings={bookings} />}
                </main>
            </div>

            <OfferteModal
                booking={offerteFor}
                onClose={() => setOfferteFor(null)}
                onCreated={() => setTab("offerten")}
            />
        </div>
    );
}
